import type { TConversationMessage } from '@cascade/api-client/conversations';
import type { TFileReadyPayload } from '@cascade/api-client/events';
import type { RequestError } from '@cascade/core/errors';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest } from '#hooks/use-latest/index.ts';
import { useApi } from '#providers/api/index.ts';
import { useAuthentication } from '#providers/authentication/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { useInvalidateQueries } from '#providers/query/index.ts';
import {
  ConversationsContext,
  ConversationThreadContext,
} from './Conversations.context.ts';
import * as helpers from './Conversations.helpers.ts';
import type {
  TAskPayload,
  TConversationsContextValue,
  TConversationsQuery,
  TConversationThreadContextValue,
  TConversationsProviderProps,
  TFileUpload,
  TMessageFeedbackPayload,
  TMessageFeedbackRequest,
  TServerAction,
} from './Conversations.types.ts';
import { useConversationList } from './use-conversation-list/index.ts';
import { useConversationMessages } from './use-conversation-messages/index.ts';
import { useConversationState } from './use-conversation-state/index.ts';
import { useConversationSocket } from './use-conversation-socket/index.ts';

const FEEDBACK_PATH = '/feedback';

export const ConversationsProvider = ({
  socket,
  http,
  userClient,
  userAgent,
  conversationId: requestedConversationId,
  children,
  onConversationIdChange,
  onConversationsDeleted,
}: TConversationsProviderProps) => {
  const api = useApi();
  const { isAuthenticated } = useAuthentication();
  const { currentProjectId, isFeatureEnabled, requireProjectId } =
    useProjects();

  const conversationsKey = useMemo(
    () =>
      helpers.getConversationsQueryKey({ project_id: currentProjectId ?? '' }),
    [currentProjectId],
  );

  const {
    conversations,
    isLoadingConversations,
    conversationsError: listError,
  } = useConversationList();

  const [roomError, setRoomError] = useState<RequestError>();

  const [appliedConversationId, setAppliedConversationId] = useState(
    requestedConversationId,
  );

  const {
    state,
    dispatchUpdate,
    hasPendingAnswer,
    sendRequest,
    removeRequest,
  } = useConversationState(requestedConversationId);

  const selectConversation = useCallback(
    (conversationId?: string) => {
      setRoomError(undefined);
      dispatchUpdate({ type: 'conversation.selected', conversationId });
    },
    [dispatchUpdate],
  );

  const isNewRequest = requestedConversationId !== appliedConversationId;
  const isEchoOfSelection = requestedConversationId === state.conversationId;

  if (isNewRequest) {
    setAppliedConversationId(requestedConversationId);
  }

  if (isNewRequest && !isEchoOfSelection) {
    selectConversation(requestedConversationId);
  }

  const {
    conversationId: currentConversationId,
    hasLoadedMessages,
    hasStaleMessages,
    messages,
  } = state;

  const onConversationIdChangeRef = useLatest(onConversationIdChange);

  const currentConversation = useMemo(
    () =>
      conversations.find(
        (conversation) =>
          conversation.conversation_id === currentConversationId,
      ),
    [conversations, currentConversationId],
  );

  const canEscalateConversation =
    isFeatureEnabled('hr_support') &&
    currentConversation !== undefined &&
    !currentConversation.is_escalated;

  const hasMessages = messages.length > 0;

  const followUpQuestions = useMemo(
    () => helpers.getFollowUpQuestions(messages),
    [messages],
  );

  const shouldLoadMessages =
    currentConversationId !== undefined &&
    (!hasLoadedMessages || hasStaleMessages);

  const loadingConversationId = shouldLoadMessages
    ? currentConversationId
    : undefined;

  const {
    conversation: loadedConversation,
    messages: loadedMessages,
    isLoadingMessages: isFetchingMessages,
    messagesError: loadError,
  } = useConversationMessages(loadingConversationId);

  const isLoadingMessages = isFetchingMessages && !hasLoadedMessages;
  const messagesError = hasLoadedMessages ? undefined : loadError;
  const hasFailedReload = hasLoadedMessages && Boolean(loadError);

  const requireConversationId = useCallback((): string => {
    if (!currentConversationId) {
      throw new Error('No conversation is selected');
    }

    return currentConversationId;
  }, [currentConversationId]);

  const invalidateQueries = useInvalidateQueries();

  const ask = useCallback(
    async ({ files, ...payload }: TAskPayload) => {
      const askPayload = {
        ...payload,
        project_id: requireProjectId(),
        conversation_id: currentConversationId,
        file_ids: files?.map((file) => file.file_id),
        user_client: userClient,
        user_agent: userAgent,
      };

      const sendQuestion = (requestId: string) =>
        socket.send('chat.ask', { request_id: requestId, payload: askPayload });

      await sendRequest(askPayload, sendQuestion, { files });
    },
    [
      socket,
      userClient,
      userAgent,
      currentConversationId,
      requireProjectId,
      sendRequest,
    ],
  );

  const applyServerEvent = (action: TServerAction) => {
    dispatchUpdate(action);

    if (action.type === 'conversation.created') {
      invalidateQueries(conversationsKey);
    }
  };

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async ({
      file,
      filename,
    }: TFileUpload): Promise<TFileReadyPayload> => {
      const uploadedFile = await api.conversationFiles.uploadConversationFile({
        request: {
          project_id: requireProjectId(),
          conversation_id: currentConversationId,
        },
        file,
      });

      return {
        file_id: uploadedFile.file_id,
        filename,
        status: uploadedFile.status,
      };
    },
  });

  const { mutateAsync: removeFile } = useMutation({
    mutationFn: (fileId: string) =>
      api.conversationFiles.deleteConversationFile({
        file_id: fileId,
        project_id: requireProjectId(),
      }),
  });

  const { mutateAsync: sendMessageFeedback } = useMutation({
    mutationFn: (payload: TMessageFeedbackPayload) =>
      http.post<TMessageFeedbackRequest, unknown>(FEEDBACK_PATH, {
        project_id: requireProjectId(),
        conversation_id: requireConversationId(),
        message_id: payload.message_id,
        type: payload.type,
        feedback: payload.feedback_message ?? '',
      }),
    onSuccess: (_result, payload) => {
      dispatchUpdate({ type: 'message.feedback.sent', payload });
    },
  });

  const queryConversations = useCallback(
    ({ sorts = [], ...filter }: TConversationsQuery) => {
      const matchingConversations = helpers.filterConversations(
        conversations,
        filter,
      );

      return helpers.sortConversations(matchingConversations, sorts);
    },
    [conversations],
  );

  const getConversationMessages = useCallback(
    async (conversationId: string): Promise<TConversationMessage[]> => {
      const { messages: conversationMessages } =
        await api.conversations.getConversationMessages({
          conversation_id: conversationId,
        });

      return conversationMessages;
    },
    [api],
  );

  const {
    mutateAsync: deleteConversations,
    isPending: isDeletingConversations,
    error: deleteConversationsError,
  } = useMutation({
    mutationFn: (conversationIds: string[]) =>
      api.conversations.deleteConversations({
        conversation_ids: conversationIds,
        project_id: requireProjectId(),
      }),
    onSuccess: (_result, conversationIds) => {
      if (
        currentConversationId !== undefined &&
        conversationIds.includes(currentConversationId)
      ) {
        selectConversation();
      }

      onConversationsDeleted?.(conversationIds);

      return invalidateQueries(conversationsKey);
    },
  });

  useConversationSocket(socket, {
    isEnabled: isAuthenticated,
    projectId: currentProjectId,
    conversationId: currentConversationId,
    onEvent: applyServerEvent,
    onRoomError: setRoomError,
    onReconnect: () => dispatchUpdate({ type: 'socket.reconnected' }),
  });

  useEffect(() => {
    if (hasFailedReload) {
      dispatchUpdate({ type: 'messages.reloadFailed' });
    }
  }, [hasFailedReload, dispatchUpdate]);

  useEffect(() => {
    if (currentConversationId === requestedConversationId) {
      return;
    }

    onConversationIdChangeRef.current?.(currentConversationId);
  }, [
    currentConversationId,
    requestedConversationId,
    onConversationIdChangeRef,
  ]);

  useEffect(() => {
    if (!loadedConversation) {
      return;
    }

    dispatchUpdate({
      type: 'messages.loaded',
      conversationId: loadedConversation.conversation_id,
      messages: loadedMessages,
    });
  }, [loadedConversation, loadedMessages, dispatchUpdate]);

  const value = useMemo<TConversationsContextValue>(
    () => ({
      conversations,
      isLoadingConversations,
      conversationsError: listError,
      currentConversationId,
      currentConversation,
      canEscalateConversation,
      isLoadingMessages,
      messagesError,
      roomError,
      hasMessages,
      hasPendingAnswer,
      isDeletingConversations,
      deleteConversationsError: deleteConversationsError ?? undefined,
      selectConversation,
      ask,
      removeRequest,
      uploadFile,
      removeFile,
      sendMessageFeedback,
      queryConversations,
      getConversationMessages,
      deleteConversations,
    }),
    [
      conversations,
      isLoadingConversations,
      listError,
      currentConversationId,
      currentConversation,
      canEscalateConversation,
      isLoadingMessages,
      messagesError,
      roomError,
      hasMessages,
      hasPendingAnswer,
      isDeletingConversations,
      deleteConversationsError,
      selectConversation,
      ask,
      removeRequest,
      uploadFile,
      removeFile,
      sendMessageFeedback,
      queryConversations,
      getConversationMessages,
      deleteConversations,
    ],
  );

  const threadValue = useMemo<TConversationThreadContextValue>(
    () => ({ messages, followUpQuestions }),
    [messages, followUpQuestions],
  );

  return (
    <ConversationsContext.Provider value={value}>
      <ConversationThreadContext.Provider value={threadValue}>
        {children}
      </ConversationThreadContext.Provider>
    </ConversationsContext.Provider>
  );
};
